import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    CircularProgress,
    Typography,
    Alert,
} from '@mui/material';
import useI18n from '../../../hooks/useI18n';
import priceListService from '../../../api/pricelist';
import TableData from '../Table/TableData';
import CellType from '../../../constants/type';

function BannerPricelistPreview({ open, onClose, filters }) {
    const { l, language: lang } = useI18n();
    const config = require('../../../config');

    const [priceList, setPriceList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 20,
        totalItems: 0,
        totalPages: 0,
        rowsPerPageOptions: [20, 50, 100],
    });

    const columns = [
        { id: 'QTARTCODE', label: l('Code'), minWidth: 50, type: CellType.STRING },
        { id: 'FNAME', label: l('Name'), minWidth: 200, type: CellType.STRING },
        { id: 'FIMG', label: l('Image'), minWidth: 80, type: CellType.IMG, align: 'center' },
        { id: 'FINSTOCK', label: l('Availability'), minWidth: 120, type: CellType.FINSTOCK, align: 'center' },
        { id: 'FPRICE1', label: l(config.price1Label) + (config.appCompno === '2' ? `,${config.currencySignPrice1}` : ''), minWidth: 100, type: CellType.PRISE_WITH_DISCOUNT, align: 'center' },
        { id: 'FPRICE2', label: l(config.price2Label) + (config.appCompno === '2' ? `,${config.currencySignPrice2}` : ''), minWidth: 80, type: CellType.STRING_WITH_CURRENCY, align: 'center' },
    ];

    useEffect(() => {
        if (open) {
            fetchPriceList();
        }
    }, [open, pagination.page, pagination.pageSize]);

    const fetchPriceList = async () => {
        setLoading(true);
        setError(null);

        try {
            // Функція для отримання значень з мультимовних об'єктів
            const extractMultilingualValues = (items) => {
                if (!Array.isArray(items)) return [];
                return items.map(item => {
                    // Якщо це об'єкт з мовами, повертаємо значення для поточної мови
                    if (typeof item === 'object' && item !== null && (item.ua || item.en || item.ru)) {
                        return item[lang] || item.ua || item.en || item.ru;
                    }
                    // Якщо це звичайний рядок, повертаємо як є
                    return item;
                });
            };

            // Перетворюємо фільтри банера в формат для API
            // Використовуємо ті ж ключі, що й BannerSettings та PriceList
            const selectedFilters = {
                LEVEL1: extractMultilingualValues(filters.categories),
                LEVEL2: extractMultilingualValues(filters.brand),
                LEVEL3: extractMultilingualValues(filters.other),
                'fishes': filters.fish_type || [],
                'types': filters.fishing_type || [],
            };

            const searchString = filters.search_strings || '';

            const result = await priceListService.findAllByUserId(
                {
                    selectedFilters: selectedFilters,
                    search: searchString,
                    page: pagination.page,
                    pageSize: pagination.pageSize,
                    showOrderedProd: false,
                },
                lang
            );

            if (result) {
                setPriceList(result.data || []);
                setPagination(prev => ({
                    ...prev,
                    page: result.currentPage,
                    totalItems: result.totalItems,
                    totalPages: result.totalPages,
                }));
            }
        } catch (err) {
            console.error('Error fetching price list preview:', err);
            setError(l('Failed to load price list') + ': ' + (err.message || ''));
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handleChangeRowsPerPage = (event) => {
        setPagination(prev => ({
            ...prev,
            pageSize: +event.target.value,
            page: 1,
        }));
    };

    const getFilterSummary = () => {
        const parts = [];
        
        // Функція для підрахунку елементів (враховує мультимовні об'єкти)
        const getItemCount = (items) => {
            if (!Array.isArray(items)) return 0;
            return items.length;
        };
        
        if (filters.categories?.length) parts.push(`${l('Categories')}: ${getItemCount(filters.categories)}`);
        if (filters.brand?.length) parts.push(`${l('Brands')}: ${getItemCount(filters.brand)}`);
        if (filters.other?.length) parts.push(`${l('Other')}: ${getItemCount(filters.other)}`);
        if (filters.fish_type?.length) parts.push(`${l('Fish type')}: ${getItemCount(filters.fish_type)}`);
        if (filters.fishing_type?.length) parts.push(`${l('Fishing type')}: ${getItemCount(filters.fishing_type)}`);
        if (filters.search_strings && filters.search_strings.trim() !== '') parts.push(`${l('Search')}: ${filters.search_strings}`);
        return parts.length > 0 ? parts.join(' | ') : l('No filters applied');
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                {l('Pricelist Preview')}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        <strong>{l('Applied filters')}:</strong> {getFilterSummary()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        <strong>{l('Total items')}:</strong> {pagination.totalItems}
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableData
                        columns={columns}
                        rows={priceList}
                        ownPagination={pagination}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        isGrouped={false}
                        tableEmptyText={l('No products match the selected filters')}
                        isPriceList={false}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{l('Close')}</Button>
            </DialogActions>
        </Dialog>
    );
}

export default BannerPricelistPreview;
